import type { Analysis } from "./analysis";

/**
 * The chart as plain text, and back again.
 *
 * A chord sheet has always been text: chord names on one line, sitting above
 * the word they change on. Editing it as anything else means editing the
 * storage rather than the thing, which is why a per-beat table felt wrong.
 *
 * Whatever spacing the author types is kept verbatim. Nothing is re-flowed on
 * save — a chord nudged two characters left stays two characters left.
 */

/** Where each word sits on its line, and which beat it is sung on. */
export interface WordSlot {
  column: number;
  beat: number;
}

export interface ChartMap {
  /** One entry per lyric line, in the order they appear in the text. */
  lines: WordSlot[][];
  /** Beats of the chords on each instrumental line, which have no word to
   *  sit above and so are matched by position instead. */
  instrumentals: number[][];
}

const CHORD_TOKEN = /^[A-G](#|b)?[A-Za-z0-9#b/+°ø()-]*$/;

/** Is this line chords rather than words? */
export function isChordLine(line: string): boolean {
  const tokens = line.trim().split(/\s+/).filter(Boolean);
  if (!tokens.length) return false;
  return tokens.every((token) => CHORD_TOKEN.test(token));
}

/** The beat whose time is nearest this moment. */
function beatAt(analysis: Analysis, seconds: number): number {
  let best = 0;
  let closest = Infinity;
  for (const beat of analysis.beats) {
    const distance = Math.abs(beat.t - seconds);
    if (distance < closest) { closest = distance; best = beat.i; }
  }
  return best;
}

/**
 * Render the chart as text, with a map from word positions back to beats.
 *
 * The layout comes from the same code the chart renders with, so what is
 * edited is exactly what was on screen.
 */
export function renderChart(
  rows: { kind: string; label?: string; chordRow: string;
          marks?: { beat: number }[];
          words: { text: string; t: number }[] }[],
  analysis: Analysis,
): { text: string; map: ChartMap } {
  const out: string[] = [];
  const lines: WordSlot[][] = [];
  const instrumentals: number[][] = [];

  for (const row of rows) {
    if (row.kind === "instrumental") {
      if (row.label) out.push(`[${row.label}]`);
      if (row.chordRow.trim()) {
        out.push(row.chordRow.replace(/\s+$/, ""));
        instrumentals.push(row.marks?.map((mark) => mark.beat) ?? []);
      }
      out.push("");
      continue;
    }

    const slots: WordSlot[] = [];
    let column = 0;
    const words: string[] = [];
    for (const word of row.words) {
      slots.push({ column, beat: beatAt(analysis, word.t) });
      words.push(word.text);
      column += word.text.length + 1;
    }
    out.push(row.chordRow.replace(/\s+$/, ""));
    out.push(words.join(" "));
    out.push("");
    lines.push(slots);
  }

  return { text: out.join("\n").replace(/\n+$/, "\n"),
           map: { lines, instrumentals } };
}

export interface ParsedChord {
  beat: number;
  chord: string;
}

/**
 * Read an edited chart back into chord-per-beat changes.
 *
 * Pairs are found structurally — a line of chord names followed by a line of
 * words — so blank lines, section markers and re-spacing all survive. Each
 * chord is assigned to the word nearest its column, which is how a reader
 * decides too: a chord a character or two off still clearly belongs to the
 * word beneath it.
 */
export function parseChart(text: string, map: ChartMap): ParsedChord[] {
  const lines = text.split("\n");
  const found: ParsedChord[] = [];
  let lyricLine = 0;
  let instrumentalLine = 0;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (!isChordLine(line)) continue;

    // The next line with any letters in it is the one these chords sit above.
    let j = i + 1;
    while (j < lines.length && !lines[j].trim()) j += 1;
    if (j >= lines.length) break;
    const words = lines[j];
    if (isChordLine(words) || !/[A-Za-zÀ-ž]/.test(words)) {
      // No words beneath: an instrumental run. Its chords keep the beats they
      // were rendered at, matched by position along the line.
      const beats = map.instrumentals[instrumentalLine] ?? [];
      instrumentalLine += 1;
      const tokens = line.trim().split(/\s+/).filter(Boolean);
      tokens.forEach((chord, index) => {
        if (index < beats.length) found.push({ beat: beats[index], chord });
      });
      continue;
    }

    const slots = map.lines[lyricLine];
    lyricLine += 1;
    if (!slots?.length) continue;

    // Where the author actually put each word, which may not be where it was.
    const authored: number[] = [];
    const pattern = /\S+/g;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(words))) authored.push(match.index);

    const chords = /\S+/g;
    while ((match = chords.exec(line))) {
      const column = match.index;
      let nearest = 0;
      let distance = Infinity;
      authored.forEach((at, index) => {
        const away = Math.abs(at - column);
        if (away < distance) { distance = away; nearest = index; }
      });
      const slot = slots[Math.min(nearest, slots.length - 1)];
      if (slot) found.push({ beat: slot.beat, chord: match[0] });
    }
    i = j;
  }

  return found.sort((a, b) => a.beat - b.beat);
}

/**
 * Turn parsed chords into a correction per beat.
 *
 * A chord holds until the next one, so the beats between them carry it too —
 * otherwise a chart with four chords would leave every other beat reading
 * whatever the engine originally heard.
 */
export function toCorrections(
  parsed: ParsedChord[], lastBeat: number,
): { beat: number; chord: string }[] {
  const rows: { beat: number; chord: string }[] = [];
  parsed.forEach((chord, index) => {
    const until = index + 1 < parsed.length ? parsed[index + 1].beat : lastBeat + 1;
    for (let beat = chord.beat; beat < until; beat += 1) {
      rows.push({ beat, chord: chord.chord });
    }
  });
  return rows;
}
